/**
 * The GLB container, read and rewritten at the BYTES level (glTF 2.0 §4:
 * 12-byte header + JSON chunk + optional BIN chunk). This module owns exactly
 * the container mechanics the derive seam needs: parse the two chunks, and
 * rebuild a GLB whose images were replaced — every bufferView re-packed with
 * its offsets rewritten, because PNG payloads change size when derived.
 *
 * It deliberately does NOT model glTF semantics (materials, textures) — that
 * classification lives in derive-glb.ts; this file knows chunks, alignment,
 * and bufferView surgery only.
 *
 * Depends on: errors. Depended on by: derive-glb, the T2 probe.
 */
import { TextureMasterError } from './errors.js';

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'
const CHUNK_BIN = 0x004e4942; // 'BIN\0'

/** The glTF JSON slices the container surgery reads and rewrites. */
export interface GlbJson {
  buffers?: { byteLength: number; uri?: string }[];
  bufferViews?: {
    buffer: number;
    byteOffset?: number;
    byteLength: number;
    byteStride?: number;
    target?: number;
  }[];
  images?: { bufferView?: number; uri?: string; mimeType?: string; name?: string }[];
  textures?: { source?: number; sampler?: number; extensions?: Record<string, unknown> }[];
  materials?: {
    name?: string;
    pbrMetallicRoughness?: {
      baseColorTexture?: { index: number };
      metallicRoughnessTexture?: { index: number };
      baseColorFactor?: number[];
      metallicFactor?: number;
      roughnessFactor?: number;
    };
    normalTexture?: { index: number; scale?: number };
    occlusionTexture?: { index: number; strength?: number };
    emissiveTexture?: { index: number };
    extensions?: Record<string, unknown>;
  }[];
  [key: string]: unknown;
}

export interface GlbContainer {
  readonly json: GlbJson;
  /** The BIN chunk payload (empty when the file has none). */
  readonly bin: Uint8Array;
}

export function parseGlbContainer(glb: ArrayBuffer): GlbContainer {
  const view = new DataView(glb);
  if (glb.byteLength < 12 || view.getUint32(0, true) !== GLB_MAGIC) {
    throw new TextureMasterError(['not a GLB: missing the glTF magic header']);
  }
  const version = view.getUint32(4, true);
  if (version !== 2) {
    throw new TextureMasterError([`GLB version ${version} — this seam speaks glTF 2.0 only`]);
  }
  let offset = 12;
  let json: GlbJson | null = null;
  let bin: Uint8Array = new Uint8Array(0);
  while (offset + 8 <= glb.byteLength) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const start = offset + 8;
    if (start + length > glb.byteLength) {
      throw new TextureMasterError(['truncated GLB: a chunk header promises bytes past the end of the file']);
    }
    const payload = new Uint8Array(glb, start, length);
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(payload)) as GlbJson;
    else if (type === CHUNK_BIN) bin = payload;
    offset = start + length;
  }
  if (json === null) throw new TextureMasterError(['GLB carries no JSON chunk']);
  return { json, bin };
}

const align4 = (n: number): number => (n + 3) & ~3;

/**
 * Serialize a container back to GLB bytes. JSON is padded with spaces, BIN
 * with zeros, both to 4-byte alignment — the spec's own rules.
 */
export function buildGlb(json: GlbJson, bin: Uint8Array): ArrayBuffer {
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPadded = align4(jsonBytes.byteLength);
  const binPadded = align4(bin.byteLength);
  const hasBin = bin.byteLength > 0;
  const total = 12 + 8 + jsonPadded + (hasBin ? 8 + binPadded : 0);

  const out = new ArrayBuffer(total);
  const view = new DataView(out);
  const bytes = new Uint8Array(out);
  view.setUint32(0, GLB_MAGIC, true);
  view.setUint32(4, 2, true);
  view.setUint32(8, total, true);
  view.setUint32(12, jsonPadded, true);
  view.setUint32(16, CHUNK_JSON, true);
  bytes.set(jsonBytes, 20);
  bytes.fill(0x20, 20 + jsonBytes.byteLength, 20 + jsonPadded); // space padding
  if (hasBin) {
    const binHeader = 20 + jsonPadded;
    view.setUint32(binHeader, binPadded, true);
    view.setUint32(binHeader + 4, CHUNK_BIN, true);
    bytes.set(bin, binHeader + 8); // zero padding is the ArrayBuffer's default
  }
  return out;
}

/** One image's bytes inside the BIN chunk, by the image's own bufferView. */
export function imageBytes(container: GlbContainer, imageIndex: number): Uint8Array {
  const image = container.json.images?.[imageIndex];
  if (image === undefined) {
    throw new TextureMasterError([`no image ${imageIndex} — the file has ${container.json.images?.length ?? 0}`]);
  }
  if (image.bufferView === undefined) {
    throw new TextureMasterError([`image ${imageIndex} is uri-referenced, not embedded — nothing to read from BIN`]);
  }
  const bv = container.json.bufferViews?.[image.bufferView];
  if (bv === undefined) {
    throw new TextureMasterError([`image ${imageIndex} names bufferView ${image.bufferView}, which does not exist`]);
  }
  const start = bv.byteOffset ?? 0;
  return container.bin.subarray(start, start + bv.byteLength);
}

/**
 * Rebuild the container with some images' payloads REPLACED. Every bufferView
 * is re-packed into a fresh BIN in its original order (4-byte aligned, the
 * accessor alignment ceiling) with offsets rewritten; nothing else in the
 * JSON moves. Refuses a replacement targeting a shared bufferView — two
 * images reading one span is a file this surgery would corrupt.
 */
/**
 * A replacement image: the bytes, and the mime type they are in when it
 * DIFFERS from what the JSON already claims. Re-encoding a JPEG source as PNG
 * (the JPEG derive path) must say so, or the file ships PNG bytes under
 * `image/jpeg` and every reader is entitled to reject it.
 */
export interface ImageReplacement {
  readonly bytes: Uint8Array;
  readonly mimeType?: string;
}

export function replaceImages(
  container: GlbContainer,
  replacements: ReadonlyMap<number, Uint8Array | ImageReplacement>,
): GlbContainer {
  const { json, bin } = container;
  const bufferViews = json.bufferViews ?? [];
  const images = json.images ?? [];

  // image index → its bufferView index, for the replaced set; refuse sharing.
  const replacedViews = new Map<number, Uint8Array>();
  const viewUsers = new Map<number, number[]>();
  images.forEach((img, i) => {
    if (img.bufferView !== undefined) {
      viewUsers.set(img.bufferView, [...(viewUsers.get(img.bufferView) ?? []), i]);
    }
  });
  const issues: string[] = [];
  const nextMime = new Map<number, string>();
  for (const [imageIndex, replacement] of replacements) {
    const payload = replacement instanceof Uint8Array ? replacement : replacement.bytes;
    const mime = replacement instanceof Uint8Array ? undefined : replacement.mimeType;
    const image = images[imageIndex];
    if (image?.bufferView === undefined) {
      issues.push(`image ${imageIndex} has no embedded bufferView to replace`);
      continue;
    }
    const users = viewUsers.get(image.bufferView) ?? [];
    if (users.length > 1) {
      issues.push(
        `image ${imageIndex} shares bufferView ${image.bufferView} with image(s) ${users.filter((u) => u !== imageIndex).join(', ')} — replacing a shared span would corrupt the others`,
      );
      continue;
    }
    replacedViews.set(image.bufferView, payload);
    if (mime !== undefined) nextMime.set(imageIndex, mime);
  }
  if (issues.length > 0) throw new TextureMasterError(issues);

  // Re-pack: originals copied, replaced views swapped, offsets rewritten.
  const spans: Uint8Array[] = [];
  let cursor = 0;
  const nextViews = bufferViews.map((bv, i) => {
    const payload = replacedViews.get(i) ?? bin.subarray(bv.byteOffset ?? 0, (bv.byteOffset ?? 0) + bv.byteLength);
    const placed = { ...bv, byteOffset: cursor, byteLength: payload.byteLength };
    spans.push(payload);
    cursor = align4(cursor + payload.byteLength);
    return placed;
  });
  const nextBin = new Uint8Array(cursor);
  nextViews.forEach((bv, i) => nextBin.set(spans[i] as Uint8Array, bv.byteOffset));

  const nextJson: GlbJson = {
    ...json,
    bufferViews: nextViews,
    // A re-encoded image declares its NEW mime type; every other image keeps what it had.
    images: images.map((img, i) => (nextMime.has(i) ? { ...img, mimeType: nextMime.get(i) as string } : img)),
    buffers: (json.buffers ?? []).map((b, i) => (i === 0 ? { ...b, byteLength: nextBin.byteLength } : b)),
  };
  return { json: nextJson, bin: nextBin };
}

/**
 * JPEG SOF peek — width/height without a decode, the JPEG twin of
 * {@link pngDimensions}. Walks the marker segments to the first frame header
 * (SOFn, excluding DHT/DAC/DRI which share the 0xC_ range).
 */
export function jpegDimensions(jpeg: Uint8Array): { width: number; height: number } | null {
  if (jpeg.byteLength < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return null;
  const view = new DataView(jpeg.buffer, jpeg.byteOffset, jpeg.byteLength);
  let o = 2;
  while (o + 9 < jpeg.byteLength) {
    if (jpeg[o] !== 0xff) {
      o += 1; // fill byte or desync — step, never guess
      continue;
    }
    const marker = jpeg[o + 1] as number;
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      o += 2; // standalone markers carry no length
      continue;
    }
    if (marker === 0xda) return null; // scan data reached with no frame header
    const length = view.getUint16(o + 2, false);
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { height: view.getUint16(o + 5, false), width: view.getUint16(o + 7, false) };
    }
    if (length < 2) return null;
    o += 2 + length;
  }
  return null;
}

/** PNG IHDR peek — width/height without a decode (the cheap classification). */
export function pngDimensions(png: Uint8Array): { width: number; height: number } | null {
  // 8-byte signature + IHDR (4 len + 4 'IHDR' + 4 width + 4 height)
  if (png.byteLength < 24) return null;
  if (png[0] !== 0x89 || png[1] !== 0x50 || png[2] !== 0x4e || png[3] !== 0x47) return null;
  const view = new DataView(png.buffer, png.byteOffset, png.byteLength);
  return { width: view.getUint32(16, false), height: view.getUint32(20, false) };
}
